export const TOLERANCE_MIN = 15;
export const LOADING_FILL = "var(--dispatch-segment-loading-service-fill, #9bb2d0)";
export const DELIVERY_FILL = "var(--dispatch-segment-delivery-service-fill, #d3c195)";
export const LOADING_INK = "#1c3048";
export const DELIVERY_INK = "#373018";
export const OUTLINE = "rgba(0,0,0,.55)";
export const OFF_COLOR = "#c2410c";
export const ADDRESS_COLOR = "#3f5585";
export const DEPOT_COLOR = "#475569";
// Pourquoi Geotab n'a rien à comparer pour un camion : page Geotab et relevé de la Consolidation (à la place du Gantt).
export const STATUS_TEXT = {
  no_stops: "Aucune étape planifiée pour ce camion ce jour-là.",
  no_times: "Heures prévues absentes dans le plan.",
  no_device: "Aucun boîtier Geotab associé à ce camion.",
  no_trips: "Aucun trajet Geotab pour ce camion ce jour-là.",
};

// Sur la cible en contour vert ; plus long ou plus tard que prévu en jaune soleil puis rouge ;
// plus court ou plus tôt en violet puis violet foncé.
const CHIP_CLASS_BY_TONE = {
  ok: "bg-white text-accent ring-1 ring-inset ring-accent",
  warn: "bg-[#ffc400] text-[#1f1a05]",
  bad: "bg-[#c5524a] text-white",
  early: "bg-[#8a63c4] text-white",
  early2: "bg-[#4c2f86] text-white",
  off: "bg-[#c2410c] text-white",
  mute: "bg-white text-slate-600 ring-1 ring-inset ring-slate-400",
  empty: "text-slate-300 font-normal",
};
const LINE_COLOR_BY_TONE = { ok: "#0f5c57", warn: "#ffc400", bad: "#c5524a", early: "#8a63c4", early2: "#4c2f86" };

export function fmtTime(minutes) {
  if (minutes == null) return "—";
  const r = Math.round(minutes);
  return `${String(Math.floor(r / 60) % 24).padStart(2, "0")}:${String(((r % 60) + 60) % 60).padStart(2, "0")}`;
}

export function fmtDuration(minutes) {
  const r = Math.round(Math.abs(minutes || 0));
  return r < 60 ? `${r} min` : `${Math.floor(r / 60)} h ${String(r % 60).padStart(2, "0")}`;
}

export function fmtDelta(minutes) {
  if (minutes == null) return "—";
  const r = Math.round(minutes);
  if (r === 0) return "0 min";
  return `${r > 0 ? "+" : "−"}${fmtDuration(r)}`;
}

export function fmtDistance(meters) {
  if (meters == null) return "—";
  return meters < 1000 ? `${Math.round(meters / 10) * 10} m` : `${(meters / 1000).toFixed(1).replace(".", ",")} km`;
}

// Lieu réel d'un arrêt Geotab (fiche Clients et sites, zone Geotab ou adresse « rue, ville ») : lignes écrites dans le bloc,
// la ville d'abord pour une adresse, puis ligne de la bulle.
export function placeLines(place) {
  if (!place?.name) return [];
  const [street, ...town] = place.name.split(", ");
  return place.source === "address" && town.length ? [town.join(", "), street] : [place.name];
}

export function placeRow(place) {
  if (!place?.name) return null;
  if (place.source === "address") return ["Adresse", place.name];
  return ["Lieu", `${place.name} · ${place.source === "zone" ? "zone Geotab" : "fiche"} à ${fmtDistance(place.distance_m)}`];
}

export function mapsUrl(lat, lon) {
  return `https://www.google.com/maps/search/?${new URLSearchParams({ api: "1", query: `${lat},${lon}` })}`;
}

// Écart réel − prévu : sur la cible jusqu'à la tolérance ; au-delà, la couleur dit le sens (plus long : jaune puis rouge,
// plus court : violet puis violet foncé) et la teinte foncée, plus du double de la tolérance.
export function deltaTone(minutes, tolerance = TOLERANCE_MIN) {
  if (minutes == null) return "empty";
  const gap = Math.abs(minutes);
  if (gap <= tolerance) return "ok";
  if (minutes > 0) return gap <= 2 * tolerance ? "warn" : "bad";
  return gap <= 2 * tolerance ? "early" : "early2";
}

// Fin de journée avant → après (réel − prévu) : 5 min et plus loin de zéro, le plan s'éloigne de la réalité.
export function dayEndTrend(before, after) {
  if (before == null || after == null) return "same";
  const change = Math.abs(after) - Math.abs(before);
  return change >= 5 ? "worse" : change <= -5 ? "better" : "same";
}

export const chipClass = (tone) => CHIP_CLASS_BY_TONE[tone] || CHIP_CLASS_BY_TONE.mute;
export const lineColor = (tone) => LINE_COLOR_BY_TONE[tone] || "#94a3b8";

// Même chauffeur que le relevé de Consolidation (celui du Dispatch, payé pour la journée) ; la session Geotab Drive
// seulement sans chauffeur prévu. Une session à un autre nom reste signalée (isOtherDriver).
export function driverOf(row) {
  return row.driver_plan || row.driver_geotab || "Chauffeur inconnu";
}

export function isOtherDriver(row) {
  // Réglé au relevé (le consolidateur a dit qui a conduit), ou session retenue « même personne » qu'un chauffeur payé.
  if (row.driver_check || (row.driver_geotab_employee_id && (row.driver_employee_ids || []).includes(row.driver_geotab_employee_id))) return false;
  const norm = (value) => (value || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
  const real = norm(row.driver_geotab);
  const planned = norm(row.driver_plan);
  return Boolean(real && planned && !real.startsWith(planned) && !planned.startsWith(real));
}

// Camion(s) conduits par le chauffeur prévu, quand ce n'est pas seulement le camion prévu.
export function truckLabel(row) {
  // Camion sans tournée au Dispatch dont les trajets ont fait celle d'un autre camion.
  if (row.route_of) return `${row.truck_name} · a fait la tournée du ${row.route_of}`;
  const others = (row.trucks_used || []).filter((u) => !u.planned).map((u) => u.name);
  if (!others.length) return row.truck_name;
  return (row.trucks_used || []).some((u) => u.planned) ? `${row.truck_name} + ${others.join(" + ")}` : `${row.truck_name} · fait avec ${others.join(" + ")}`;
}

// Tronçons de trace : un par camion conduit, bornés à ses heures (le dernier reste ouvert en direct).
export function traceSegments(row, { live = false } = {}) {
  const used = row.trucks_used || [];
  if (!used.length) return row.device_id ? [{ deviceId: row.device_id }] : [];
  return used.map((u, i) => ({ deviceId: u.device_id, fromMin: u.start - 5, toMin: live && i === used.length - 1 ? undefined : u.end + 5 }));
}

// Route pour se rendre à chaque site, puis au dépôt : de la fin de l'étape précédente à l'arrivée. Les arrêts Geotab
// intermédiaires sont retirés du réel (comme real_drive_min et le Calibrage), le dîner prévu, du prévu. Le réel reste vide,
// avec sa raison, si le camion arrive d'un autre site que prévu ou si l'arrivée ou le départ précédent est inconnu.
export function driveLegs(row) {
  const { comparison: cmp, plan } = row;
  const done = cmp.sites.filter((s) => s.real_arrival != null).sort((a, b) => a.real_arrival - b.real_arrival);
  return [...cmp.sites, { plan_arrival: plan.return_at, real_arrival: cmp.real_return }].map((to, i) => {
    const from = cmp.sites[i - 1];
    const leg = { plan: to.plan_arrival - (from ? from.plan_departure + (from.lunch_after || 0) : plan.departure), real: null };
    const start = from ? from.real_departure : cmp.real_departure;
    if (to.real_arrival == null) return { ...leg, why: to.idx ? "non détecté" : cmp.in_progress ? "en cours" : "pas de retour" };
    if (done[to.idx ? done.indexOf(to) - 1 : done.length - 1] !== from) return { ...leg, why: from && from.real_arrival == null ? "précédent non vu" : "ordre modifié" };
    // Arrêt court : son arrivée comprend le temps passé dans la cour, pas un trajet à mesurer.
    if (to.short || start == null) return { ...leg, why: "inconnu" };
    const stops = cmp.visits.filter((v) => v.arr >= start && v.arr < to.real_arrival).reduce((sum, v) => sum + v.dwell, 0);
    return { ...leg, real: to.real_arrival - start - stops };
  });
}

// Moyennes prévue et réelle des paires [prévu, réel] mesurées des deux côtés, arrondies à la minute : l'écart tombe juste
// avec les valeurs affichées. null sans aucune mesure.
export function meanDurations(pairs) {
  const done = pairs.filter(([, real]) => real != null);
  if (!done.length) return null;
  const [plan, real] = [0, 1].map((k) => Math.round(done.reduce((sum, pair) => sum + pair[k], 0) / done.length));
  return { plan, real, gap: real - plan, count: done.length, of: pairs.length };
}

export function siteKindLabel(kind) {
  return kind === "loading" ? "Chargement" : kind === "delivery" ? "Livraison" : "Dépôt";
}

// Même numérotation que le Gantt du Dispatch : « 2-5 » pour un site regroupant les étapes 2 à 5.
export function stepLabel(steps) {
  if (!steps?.length) return "";
  const first = steps[0];
  const last = steps[steps.length - 1];
  return first === last ? String(first) : `${first}-${last}`;
}
